import { isAxiosError } from 'axios'
import { create } from 'zustand'
import { registrarPerdidaDeSesion } from '../api/axios'
import { APP_SLUG } from '../config'
import { authService } from '../services/authService'
import { User } from '../types/auth'

/**
 * Estado de la sesión. La fuente de verdad es el servidor (GET /api/me/): acá solo
 * se guarda la última respuesta. Vive en un store (y no solo en un contexto de
 * React) para que el cliente HTTP pueda avisar desde fuera de los componentes que
 * la sesión venció.
 */
export type EstadoSesion = 'desconocido' | 'verificando' | 'con-sesion' | 'sin-sesion'

/** Clave de localStorage con la que una pestaña avisa a las otras que la sesión cambió. */
export const CLAVE_AVISO_SESION = `${APP_SLUG}:sesion`

interface AuthState {
  user: User | null
  estado: EstadoSesion
  error: string | null
  verificar: () => Promise<void>
  login: (username: string, password: string) => Promise<void>
  /** Para el dev-login y cualquier otra vía que ya trae el usuario. */
  entrar: (user: User) => void
  logout: () => Promise<void>
}

let verificacionEnCurso: Promise<void> | null = null

const avisarOtrasPestanas = () => {
  try {
    localStorage.setItem(CLAVE_AVISO_SESION, String(Date.now()))
  } catch {
    // Sin localStorage (modo privado estricto): las otras pestañas se enteran al recargar.
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  estado: 'desconocido',
  error: null,

  verificar: () => {
    // Varios componentes pueden pedirlo a la vez al montar: una sola petición.
    verificacionEnCurso ??= (async () => {
      set({ estado: 'verificando', error: null })
      try {
        const { user } = await authService.me()
        set({ user, estado: 'con-sesion' })
      } catch (err) {
        const sinSesion = isAxiosError(err) && err.response?.status === 401
        set({ user: null, estado: 'sin-sesion', error: sinSesion ? null : 'No se pudo contactar al servidor.' })
      } finally {
        verificacionEnCurso = null
      }
    })()
    return verificacionEnCurso
  },

  login: async (username, password) => {
    const { user } = await authService.login(username, password)
    set({ user, estado: 'con-sesion', error: null })
    avisarOtrasPestanas()
  },

  entrar: (user) => {
    set({ user, estado: 'con-sesion', error: null })
    avisarOtrasPestanas()
  },

  logout: async () => {
    try {
      await authService.logout()
    } finally {
      set({ user: null, estado: 'sin-sesion', error: null })
      avisarOtrasPestanas()
    }
  },
}))

// Un 401 en cualquier llamada (sesión vencida o cerrada en otra pestaña) deja al
// store sin sesión; RequireAuth se encarga de mandar al login.
registrarPerdidaDeSesion(() => useAuthStore.setState({ user: null, estado: 'sin-sesion' }))
